/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useContext} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {Box, Container, Heading, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AuthHelpers, useAuthHelper, useCustomerType} from '@salesforce/commerce-sdk-react'
import {useForm} from 'react-hook-form'
import {useLocation, useHistory} from 'react-router-dom'
import useMultiSite from '../../hooks/use-multi-site'
import useSiteType from '../../hooks/use-site-type'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import RegisterForm from '../../components/register'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useEinstein from '../../hooks/use-einstein'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {
    setLocalStorageJSONItem,
    findDialCodeByCountry,
    removePrefixFromKeys
} from './../../utils/utils'
import {IPContext} from '../../custom-contexts'
import useShipToCountry from './../../hooks/use-ship-to-country'
import {useXSSProtection} from './../../hooks/use-xss-protection.js'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'
import {OUTLET_DISPATCH_URL} from '../../constants.js'

const Registration = () => {
    const {shipToCountry} = useShipToCountry()
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const {isRegistered} = useCustomerType()
    const form = useForm()
    const einstein = useEinstein()
    const {pathname} = useLocation()
    const {isUS, isOutlet} = useSiteType()
    const {locale, buildUrl} = useMultiSite()
    const register = useAuthHelper(AuthHelpers.Register)
    const {isIPBlocked} = useContext(IPContext)
    const registrationFormPrefix = 'registration_'
    const history = useHistory()
    const {validateXssForm} = useXSSProtection()
    const {triggerNewsletterSubscriptionEvent, triggerSignUpEvent} = useGTM()

    const submitForm = async (values) => {
        if (isIPBlocked) {
            const path = buildUrl(isOutlet ? OUTLET_DISPATCH_URL : '/')
            history.push(path)
            return
        }

        const data = removePrefixFromKeys(values, registrationFormPrefix)

        const isXssDetected = validateXssForm(data)
        if (isXssDetected) {
            return
        }

        const phoneCountry = data.phoneCountry || data.countryCode
        const body = {
            customer: {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                ...(data.birthday && {birthday: data.birthday}),
                // phone format: phone country dial & phone divided whitespace
                ...(data.phone && {
                    phoneHome: `${findDialCodeByCountry(phoneCountry)} ${data.phone}`
                }),
                c_phoneHomeCountryCode: phoneCountry,
                c_shareConsent: isUS ? true : data.shareConsent,
                c_shareConsentDate: new Date(),
                c_marketingConsent: data.marketingConsent,
                c_marketingConsentDate: new Date(),
                ...(data.marketingConsent &&
                    (isOutlet ? {c_outletNewsletter: true} : {c_fullPriceNewsletter: true})),
                c_newsletterCountryCode: data.marketingConsent ? shipToCountry.id : '',
                login: data.email,
                // preferred locale is set with the site locale on account creation
                preferredLocale: locale.id.replace('-', '_')
            },
            password: data.password
        }

        try {
            await register.mutateAsync(body, {})

            /* GTM Integration */
            triggerNewsletterSubscriptionEvent('body')
            triggerSignUpEvent()
        } catch (err) {
            const json = await err.response.json()
            if (json?.type?.includes('login-already-in-use')) {
                form.setError('global', {
                    type: 'manual',
                    message: formatMessage({
                        id: 'registration_page.error.email_already_used',
                        defaultMessage: 'This email is already registred.'
                    })
                })
                return
            } else {
                form.setError('global', {type: 'manual', message: formatMessage(API_ERROR_MESSAGE)})
            }
        }
    }

    useEffect(() => {
        if (isRegistered) {
            setLocalStorageJSONItem('isAccountRegistrationForm', true)
            navigate('/account')
        }
    }, [isRegistered])

    /**************** Einstein ****************/
    useEffect(() => {
        einstein.sendViewPage(pathname)
    }, [])

    return (
        <Box data-testid="registration-page" layerStyle="userpage">
            <Seo
                title={formatMessage({
                    id: 'registration_page.seo.title',
                    defaultMessage: 'Registration'
                })}
                description={formatMessage({
                    id: 'registration_page.seo.desc',
                    defaultMessage: 'Customer sign up'
                })}
            />
            <Container px={[16, 16, 24, 24]} py={24} width={['100%']} maxW="400px" bg="white">
                <Stack spacing={32}>
                    <Heading as="h1" size="4xl" textTransform="uppercase">
                        <FormattedMessage
                            defaultMessage="Create account"
                            id="registration_page.heading"
                        />
                    </Heading>

                    <RegisterForm
                        submitForm={submitForm}
                        form={form}
                        clickSignIn={() => navigate('/login')}
                    />
                </Stack>
            </Container>
        </Box>
    )
}

Registration.getTemplateName = () => 'registration'

Registration.propTypes = {
    match: PropTypes.object
}

export default Registration
